import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Cpu, FileDown, Loader2, Mail, Save, Sparkles, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShareLink } from "@/components/ShareLink";
import { OrnamentDivider } from "@/components/OrnamentDivider";
import { dietLabel } from "@/lib/format";
import { getInvitation } from "@/lib/api";
import { t, tf } from "@/i18n";

const Stat = ({ label, value, sub, testId }) => (
  <div className="surface rounded-xl p-4">
    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--muted-ink)]">
      {label}
    </p>
    <p className="font-num mt-1 text-2xl font-semibold" data-testid={testId}>
      {value}
    </p>
    {sub ? <p className="mt-0.5 text-[11.5px] text-[color:var(--ink-soft)]">{sub}</p> : null}
  </div>
);

const SmallLabel = ({ children }) => (
  <Label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">
    {children}
  </Label>
);

export const OverviewTab = ({
  payload,
  plan,
  rsvpUrl,
  adminUrl,
  onPatch,
  onGenerate,
  generating,
  onExportPdf,
  onGoToPlan,
}) => {
  const wedding = payload.wedding;
  const counts = payload.counts;
  const [tables, setTables] = useState(wedding.n_tables);
  const [seats, setSeats] = useState(wedding.seats_per_table);
  const [saving, setSaving] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [contact, setContact] = useState(wedding.contact_email || "");
  const [savingContact, setSavingContact] = useState(false);
  const [seatPlan, setSeatPlan] = useState(
    () => wedding.seat_plan || Array(wedding.n_tables).fill(wedding.seats_per_table)
  );
  const [headTable, setHeadTable] = useState(wedding.head_table || 0);
  const [kidsTable, setKidsTable] = useState(wedding.children_table || 0);
  const [kidsAge, setKidsAge] = useState(wedding.children_min_age ?? 4);
  const [prefs, setPrefs] = useState(
    () => wedding.preferences || { family_mixing: 0.5, side_mixing: 0.5 }
  );

  useEffect(() => {
    let active = true;
    getInvitation(wedding.id, rsvpUrl)
      .then((data) => active && setInvitation(data))
      .catch(() => active && setInvitation(null));
    return () => {
      active = false;
    };
  }, [wedding.id, rsvpUrl]);

  // Adding or removing tables has to grow or trim the per-table plan with it,
  // otherwise the two disagree and the server falls back to a uniform room.
  const resizePlan = (count, fallback) =>
    Array.from({ length: count }, (_, i) => Number(seatPlan[i]) || Number(fallback) || 10);

  const save = async () => {
    setSaving(true);
    try {
      const count = Number(tables);
      await onPatch({
        n_tables: count,
        seats_per_table: Number(seats),
        seat_plan: resizePlan(count, seats),
        head_table: Number(headTable),
        children_table: Number(kidsTable),
        children_min_age: Number(kidsAge),
        preferences: prefs,
      });
      toast.success(t("overview.roomUpdated"));
    } catch (error) {
      toast.error(t("overview.roomFailed"));
    } finally {
      setSaving(false);
    }
  };

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await onPatch({ contact_email: contact.trim() });
      toast.success(t("overview.contactSaved"));
    } catch (error) {
      toast.error(t("overview.roomFailed"));
    } finally {
      setSavingContact(false);
    }
  };

  const totalSeats = resizePlan(Number(tables) || 0, seats).reduce((a, b) => a + b, 0);
  const notEnoughSeats = totalSeats < counts.guests;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label={t("overview.guestsInvited")} value={counts.guests} testId="stat-guests" />
        <Stat
          label={t("overview.answeredForm")}
          value={`${counts.answered}`}
          sub={tf("overview.ofList", { p: counts.response_rate })}
          testId="stat-answered"
        />
        <Stat
          label={t("overview.seats")}
          value={counts.seats}
          sub={tf("overview.tablesTimes", { n: wedding.n_tables, s: wedding.seats_per_table })}
          testId="stat-seats"
        />
        <Stat
          label={t("overview.plan")}
          value={payload.has_plan ? t("overview.ready") : t("overview.notYet")}
          sub={plan ? `${plan.runtime_seconds}s · ${plan.model}` : t("overview.generateWhen")}
          testId="stat-plan"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <section className="surface rounded-xl p-5">
          <p className="eyebrow">{t("overview.share")}</p>
          <h2 className="mt-1 text-xl font-semibold">{t("overview.twoLinks")}</h2>
          <div className="mt-4 space-y-3">
            <ShareLink
              label={t("overview.guestForm")}
              url={rsvpUrl}
              hint={t("overview.guestFormHint")}
              testId="share-rsvp"
            />
            <ShareLink
              label={t("overview.workspace")}
              url={adminUrl}
              hint={t("overview.workspaceHint")}
              testId="share-admin"
            />
          </div>

          <div className="mt-5">
            <SmallLabel>{t("overview.contact")}</SmallLabel>
            <p className="mt-1 text-[12px] text-[color:var(--ink-soft)]">{t("overview.contactHint")}</p>
            <div className="mt-2 flex gap-2">
              <Input
                type="email"
                value={contact}
                onChange={(event) => setContact(event.target.value)}
                placeholder="amelie.julien@example.fr"
                className="h-10 bg-white"
                data-testid="contact-email-input"
              />
              <Button
                variant="outline"
                onClick={saveContact}
                disabled={savingContact || contact.trim() === (wedding.contact_email || "")}
                data-testid="save-contact-button"
              >
                {savingContact ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("overview.contactSave")}
              </Button>
            </div>
          </div>

          <OrnamentDivider label={t("overview.invitation")} className="!my-5" />
          <p className="text-[13px] text-[color:var(--ink-soft)]">{t("overview.invitationHint")}</p>
          {invitation ? (
            <>
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">
                {t("overview.subject")}
              </p>
              <p className="text-[13.5px] font-medium" data-testid="invitation-subject">
                {invitation.subject}
              </p>
              <textarea
                readOnly
                value={invitation.text}
                rows={9}
                className="scroll-thin mt-3 w-full rounded-lg border p-3 text-[12.5px] leading-relaxed"
                style={{ borderColor: "var(--line)", backgroundColor: "var(--paper)" }}
                data-testid="invitation-text"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={async () => {
                    await navigator.clipboard.writeText(
                      `${invitation.subject}\n\n${invitation.text}`
                    );
                    toast.success(t("overview.copied"));
                  }}
                  data-testid="invitation-copy-button"
                >
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> {t("overview.copy")}
                </Button>
                <Button variant="outline" size="sm" asChild data-testid="invitation-mailto-button">
                  <a href={invitation.mailto}>
                    <Mail className="mr-1.5 h-3.5 w-3.5" /> {t("overview.openMail")}
                  </a>
                </Button>
              </div>
              <p className="mt-2 text-[11.5px] text-[color:var(--muted-ink)]">{invitation.tip}</p>
            </>
          ) : (
            <p className="mt-3 text-[12.5px] text-[color:var(--muted-ink)]">{t("overview.loadingMessage")}</p>
          )}
        </section>

        <section className="surface rounded-xl p-5">
          <p className="eyebrow">{t("overview.room")}</p>
          <h2 className="mt-1 text-xl font-semibold">{t("overview.tablesSeats")}</h2>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <SmallLabel>{t("overview.tables")}</SmallLabel>
              <Input
                type="number"
                min={1}
                max={60}
                value={tables}
                onChange={(event) => setTables(event.target.value)}
                className="font-num mt-1.5 h-10 bg-white"
                data-testid="settings-tables-input"
              />
            </div>
            <div>
              <SmallLabel>{t("overview.seatsPerTable")}</SmallLabel>
              <Input
                type="number"
                min={2}
                max={24}
                value={seats}
                onChange={(event) => setSeats(event.target.value)}
                className="font-num mt-1.5 h-10 bg-white"
                data-testid="settings-seats-input"
              />
            </div>
          </div>
          <p
            className="font-num mt-2 text-[12.5px]"
            style={{ color: notEnoughSeats ? "var(--danger)" : "var(--ink-soft)" }}
            data-testid="settings-seats-total"
          >
            {tf("overview.seatsFor", { total: totalSeats, n: counts.guests })}
            {notEnoughSeats ? t("overview.notEnough") : ""}
          </p>
          <div className="mt-4">
            <SmallLabel>{t("overview.seatsEach")}</SmallLabel>
            <p className="mt-1 text-[12px] text-[color:var(--ink-soft)]">{t("overview.seatsEachHint")}</p>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="seat-plan-editor">
              {Array.from({ length: Number(tables) || 0 }, (_, i) => (
                <label
                  key={i}
                  className="flex items-center gap-1 rounded-lg border px-1.5 py-1"
                  style={{
                    borderColor: Number(headTable) === i + 1 ? "var(--gild)" : "var(--line)",
                    backgroundColor: "#ffffff",
                  }}
                  title={`${t("overview.table")} ${i + 1}`}
                >
                  <span className="font-num text-[10px] text-[color:var(--muted-ink)]">{i + 1}</span>
                  <input
                    type="number"
                    min={2}
                    max={24}
                    value={seatPlan[i] ?? seats}
                    onChange={(event) =>
                      setSeatPlan((prev) => {
                        const next = [...prev];
                        next[i] = event.target.value;
                        return next;
                      })
                    }
                    className="font-num w-9 border-0 p-0 text-center text-[13px] outline-none"
                    data-testid={`seat-plan-${i + 1}`}
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <SmallLabel>{t("overview.headTable")}</SmallLabel>
            <p className="mt-1 text-[12px] text-[color:var(--ink-soft)]">{t("overview.headTableHint")}</p>
            <select
              value={headTable}
              onChange={(event) => setHeadTable(event.target.value)}
              className="mt-2 h-10 w-full rounded-lg border bg-white px-2 text-[13.5px]"
              style={{ borderColor: "var(--line)" }}
              data-testid="head-table-select"
            >
              <option value={0}>{t("overview.noHeadTable")}</option>
              {Array.from({ length: Number(tables) || 0 }, (_, i) => (
                <option key={i} value={i + 1}>
                  {t("overview.table")} {i + 1}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4">
            <SmallLabel>{t("overview.kidsTable")}</SmallLabel>
            <p className="mt-1 text-[12px] text-[color:var(--ink-soft)]">{t("overview.kidsHint")}</p>
            <div className="mt-2 flex gap-2">
              <select
                value={kidsTable}
                onChange={(event) => setKidsTable(event.target.value)}
                className="h-10 flex-1 rounded-lg border bg-white px-2 text-[13.5px]"
                style={{ borderColor: "var(--line)" }}
                data-testid="children-table-select"
              >
                <option value={0}>{t("overview.noKidsTable")}</option>
                {Array.from({ length: Number(tables) || 0 }, (_, i) => (
                  <option key={i} value={i + 1}>
                    {t("overview.table")} {i + 1}
                  </option>
                ))}
              </select>
              <label
                className="flex items-center gap-1.5 rounded-lg border bg-white px-2 text-[12.5px]"
                style={{ borderColor: "var(--line)" }}
                title={t("overview.kidsTitle")}
              >
                {t("overview.kidsFrom")}
                <input
                  type="number"
                  min={0}
                  max={18}
                  value={kidsAge}
                  onChange={(event) => setKidsAge(event.target.value)}
                  disabled={!Number(kidsTable)}
                  className="font-num w-9 border-0 p-0 text-center text-[13.5px] outline-none disabled:opacity-40"
                  data-testid="children-min-age"
                />
                {t("overview.kidsYears")}
              </label>
            </div>
          </div>

          <div className="mt-4">
            <SmallLabel>{t("overview.feel")}</SmallLabel>
            <p className="mt-1 text-[12px] text-[color:var(--ink-soft)]">{t("overview.feelHint")}</p>
            {[
              { id: "family_mixing", left: t("overview.keepFamilies"), right: t("overview.mixFamilies") },
              { id: "side_mixing", left: t("overview.keepSides"), right: t("overview.mixSides") },
            ].map((slider) => (
              <div key={slider.id} className="mt-3">
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.25}
                  value={prefs[slider.id] ?? 0.5}
                  onChange={(event) =>
                    setPrefs((prev) => ({ ...prev, [slider.id]: Number(event.target.value) }))
                  }
                  className="w-full"
                  data-testid={`pref-${slider.id}`}
                />
                <div className="flex justify-between text-[11px] text-[color:var(--muted-ink)]">
                  <span>{slider.left}</span>
                  <span>{slider.right}</span>
                </div>
              </div>
            ))}
          </div>

          <Button
            className="mt-3"
            variant="outline"
            onClick={save}
            disabled={saving}
            data-testid="save-room-button"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {t("overview.saveRoom")}
          </Button>
        </section>
      </div>

      <OrnamentDivider label={payload.has_plan ? t("overview.yourPlan") : t("overview.nextStep")} />

      <section className="surface rounded-xl p-5">
        {payload.has_plan && plan ? (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_auto]">
            <div>
              <h2 className="text-xl font-semibold">
                {tf("overview.seatedAcross", { seated: plan.stats.seated, n: plan.n_tables })}
              </h2>
              <p className="mt-1.5 text-[13.5px] text-[color:var(--ink-soft)]">
                {plan.validation.conflict_count === 0
                  ? t("overview.everyAvoid")
                  : tf("overview.avoidToResolve", { n: plan.validation.conflict_count })}
                {" · "}
                {tf("overview.wishesMet", {
                  a: plan.stats.wishes_satisfied,
                  b: plan.stats.wishes_total,
                })}
                {" · "}
                {tf("overview.toDecide", { n: plan.unseated.length })}
              </p>
              {Object.keys(plan.stats.diet_summary || {}).length ? (
                <div className="mt-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--muted-ink)]">
                    {t("overview.kitchen")}
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {Object.entries(plan.stats.diet_summary)
                      .sort((a, b) => b[1] - a[1])
                      .map(([tag, count]) => (
                        <span
                          key={tag}
                          className="rounded-full border px-2 py-0.5 text-[11px]"
                          style={{ borderColor: "var(--line)", color: "var(--ink-soft)" }}
                        >
                          {count} × {dietLabel(tag)}
                        </span>
                      ))}
                  </div>
                </div>
              ) : null}
              <p className="font-num mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[color:var(--muted-ink)]">
                <span className="inline-flex items-center gap-1">
                  <Sparkles className="h-3 w-3" style={{ color: "var(--gild)" }} /> {plan.model}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Cpu className="h-3 w-3" style={{ color: "var(--sage-dark)" }} />{" "}
                  {tf("overview.placementInCode", { n: plan.search_stats?.moves || 0 })}
                </span>
                <span>{tf("overview.generatedIn", { s: plan.runtime_seconds })}</span>
              </p>
              {plan.run_stats ? (
                <p
                  className="font-num mt-1 text-[10.5px] text-[color:var(--muted-ink)]"
                  data-testid="overview-run-stats"
                >
                  {tf("overview.aiCalls", {
                    n: plan.run_stats.llm?.calls || 0,
                    s: plan.run_stats.llm?.seconds || 0,
                  })}
                  {plan.run_stats.llm?.retries
                    ? ` · ${tf("overview.retried", { n: plan.run_stats.llm.retries })}`
                    : ""}
                  {plan.run_stats.llm?.failures
                    ? ` · ${tf("overview.fellBack", { n: plan.run_stats.llm.failures })}`
                    : ""}{" "}
                  · {t("overview.embeddings")} {plan.run_stats.embedding_model} · {t("overview.grouping")}{" "}
                  {plan.run_stats.cluster_source === "llm" ? t("overview.byAi") : t("overview.byCode")}
                  {plan.run_stats.pins_kept
                    ? ` · ${tf("overview.pinsKept", { n: plan.run_stats.pins_kept })}`
                    : ""}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-2">
              <Button onClick={onGoToPlan} data-testid="overview-open-plan-button">
                {t("overview.openPlan")}
              </Button>
              <Button variant="outline" onClick={onExportPdf} data-testid="overview-export-pdf-button">
                <FileDown className="mr-2 h-4 w-4" /> {t("overview.exportPdf")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold">{t("overview.readyWhen")}</h2>
              <p className="mt-1.5 max-w-xl text-[13.5px] text-[color:var(--ink-soft)]">
                {counts.guests === 0
                  ? t("overview.addFirst")
                  : tf("overview.partial", { guests: counts.guests, answered: counts.answered })}
              </p>
            </div>
            <Button
              size="lg"
              onClick={onGenerate}
              disabled={generating || counts.guests === 0 || notEnoughSeats}
              data-testid="overview-generate-plan-button"
            >
              {generating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Users className="mr-2 h-4 w-4" />
              )}
              {t("overview.generatePlan")}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
};

export default OverviewTab;
